import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { SensorsListResult } from 'src/interfaces'
import { Entities } from 'src/enums'
import { useApiInstance } from './useApiInstance'
import { buildRequestParams, ObjectType, transformPage, transformPerPage } from './helpers'

interface RequestParams {
  params?: Partial<AdminSensorsGetParams>
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>
  options?: Omit<UseQueryOptions<unknown, Error, SensorsListResult>, 'queryKey' | 'queryFn'>
}

interface AdminSensorsGetParams {
  limit: number
  offset: number
  sort: string
  projectId: number
  contractorId: number
  stateId: number
  typeId: number
  lastHeard: string
  lastPing: string
  lastPingMoreThan: number
  excludeStaging: boolean
  synchronizationPeriod: string
  accelerationTime: string
  batteryTime: string
  dewpointTime: string
  humidityTime: string
  lightTime: string
  locationTime: string
  locationGpsTime: string
  orientationTime: string
  pressureTime: string
  signalStrengthTime: string
  temperatureTime: string
  batteryLevel: number
  signalStrength: number
  search: string
  currentSensorConfiguration: string
  isBleEnabled: boolean
  sensorFirmwareVersion: string
  isAvailableInThingspace: boolean
  activationStatus: string
  activatedMoreThanMonths: number
  noPingMoreThanMonths: number
  environment: string
  orderNumber: string
  orderDateFrom: string
  orderDateTo: string
  orderPart: number
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  projectId: 'number',
  contractorId: 'number',
  stateId: 'number',
  typeId: 'number',
  lastHeard: 'string',
  lastPing: 'string',
  lastPingMoreThan: 'number',
  excludeStaging: 'boolean',
  synchronizationPeriod: 'string',
  accelerationTime: 'string',
  batteryTime: 'string',
  dewpointTime: 'string',
  humidityTime: 'string',
  lightTime: 'string',
  locationTime: 'string',
  locationGpsTime: 'string',
  orientationTime: 'string',
  pressureTime: 'string',
  signalStrengthTime: 'string',
  temperatureTime: 'string',
  batteryLevel: 'number',
  signalStrength: 'number',
  search: 'string',
  currentSensorConfiguration: 'string',
  isBleEnabled: 'boolean',
  sensorFirmwareVersion: 'string',
  isAvailableInThingspace: 'boolean',
  activationStatus: 'string',
  activatedMoreThanMonths: 'number',
  noPingMoreThanMonths: 'number',
  orderNumber: 'string',
  orderDateFrom: 'string',
  orderDateTo: 'string',
  orderPart: 'number',
  environment: 'string'
}

export const useSensorListRequest = ({ params = {}, paramsTransform = [], options }: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [transformPerPage, transformPage, ...paramsTransform].reduce((param, transform) => transform(param), params)
  const queryParams = buildRequestParams<AdminSensorsGetParams>(transformedParams, schema)
  return useQuery<unknown, Error, SensorsListResult>(
    [Entities.Sensors, JSON.stringify(params)],
    async () => {
      const {
        limit,
        offset,
        sort,
        projectId,
        contractorId,
        stateId,
        typeId,
        lastHeard,
        lastPing,
        lastPingMoreThan,
        excludeStaging,
        synchronizationPeriod,
        accelerationTime,
        batteryTime,
        dewpointTime,
        humidityTime,
        lightTime,
        locationTime,
        locationGpsTime,
        orientationTime,
        pressureTime,
        signalStrengthTime,
        temperatureTime,
        batteryLevel,
        signalStrength,
        search,
        currentSensorConfiguration,
        isBleEnabled,
        sensorFirmwareVersion,
        isAvailableInThingspace,
        activationStatus,
        activatedMoreThanMonths,
        noPingMoreThanMonths,
        orderNumber,
        orderDateFrom,
        orderDateTo,
        orderPart,
        environment
      } = queryParams
      const { data } = await api.v1AdminSensorsGet(
        limit,
        offset,
        sort,
        projectId,
        contractorId,
        stateId,
        lastHeard,
        lastPing,
        lastPingMoreThan,
        // `environment` supersedes the legacy `excludeStaging` flag — send only one of them
        environment !== undefined ? undefined : excludeStaging,
        environment as 'all' | 'staging' | 'production' | undefined,
        synchronizationPeriod,
        accelerationTime,
        batteryTime,
        dewpointTime,
        humidityTime,
        lightTime,
        locationTime,
        locationGpsTime,
        orientationTime,
        pressureTime,
        signalStrengthTime,
        temperatureTime,
        batteryLevel,
        signalStrength,
        search,
        currentSensorConfiguration,
        isBleEnabled,
        sensorFirmwareVersion,
        String(params.isAvailableInThingspace) !== 'all' ? isAvailableInThingspace : undefined,
        activationStatus && activationStatus !== 'all' ? activationStatus : undefined,
        typeId,
        activatedMoreThanMonths,
        noPingMoreThanMonths,
        orderNumber,
        orderDateFrom,
        orderDateTo,
        orderPart
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options
    }
  )
}
